import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { retry, firstValueFrom, Subject, filter, takeUntil, timer, race, take, timeout } from 'rxjs';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { setMsalTenantId } from './core/auth/msal.factory';
import { prepareInstanceAdminEntry } from './core/auth/instance-admin-entry';
import { CLOCK_SKEW_BUFFER_SECONDS } from './core/auth/constants/clock-skew.constant';
import { AuthorizationService } from './core/authorization/services/authorization.service';
import { AuthUserService } from './core/auth/auth-user.service';
import { OrganizationContextService } from './core/auth/organization-context.service';
import { SCOPES } from './core/authorization/constants/scopes.constant';
import { SESSION_STORAGE_KEYS } from './core/session_storage/constants/session-storage-keys.constant';
import { ConfigService } from './core/config/config.service';
import { AzureTenant } from './shared/models/azureTenant';
import { ConnectedTenants } from './shared/models/connectedTenants';
import { TenantResponse } from './shared/models/tenantResponse';
import { ToastService } from '@shared/services/toast/toast.service';
import { DataService } from '@shared/services/data/data.service';
import { ThemeService } from '@shared/services/theme/theme.service';
import { PrimeNG } from 'primeng/config';
import { AccountSwitchService, AccountSwitchEvent } from './shared/services/account-switch/account-switch.service';
import { AccountSwitchDialogComponent } from './shared/components/account-switch-dialog/account-switch-dialog.component';
import { RoleContextService, PortalRole } from '@shared/services/role-context.service';
import {
  clearAllStoredAccountSelections,
  clearStoredAccountSelection,
  persistStoredAccountSelection,
  readStoredAccountHomeId,
  resolveAccountForTenant,
} from './core/auth/account-selection.util';
import { Bind } from 'primeng/bind';
import { Toast } from 'primeng/toast';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ConfigurationComponent } from './shared/components/configuration/configuration.component';
import { ShellComponent } from './shared/components/shell/shell.component';
import { TenantSelectionComponent } from './shared/components/tenant-selection/tenant-selection.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    Bind,
    Toast,
    ProgressSpinner,
    ConfigurationComponent,
    ShellComponent,
    RouterOutlet,
    TenantSelectionComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroying$ = new Subject<void>();
  private authFlowInProgress = false;
  private redirectRecoveryAttempted = false;
  private accountSwitchDialogRef: DynamicDialogRef | null = null;
  private hasAutoSelectedSingleTenant = false;
  private readonly AUTH_TIMEOUT_MS = 15000; // 15 second timeout (per PR description)
  private readonly TENANT_DISCOVERY_TIMEOUT_MS = 10000; // 10 second timeout for tenant API

  isTenantSwitched = false;
  isNoTenantConnected = false;
  isConsent = false;
  isInstanceAdmin = false;
  isOnboarding = false;
  isInitializing = true;
  sessionExpired = false;
  connectedTenants: AzureTenant[] = [];
  selectedTenantId: string | undefined;

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private httpClient: HttpClient,
    private auth: AuthUserService,
    private authorizationService: AuthorizationService,
    private readonly configService: ConfigService,
    private readonly themeService: ThemeService,
    private toastMessageService: ToastService,
    private messageService: MessageService,
    private dataService: DataService,
    private primeng: PrimeNG,
    private dialogService: DialogService,
    private accountSwitchService: AccountSwitchService,
    private router: Router,
    private roleContextService: RoleContextService,
    private organizationContext: OrganizationContextService,
  ) {}

  ngOnInit(): void {
    this.primeng.setConfig({
      translation: this.dataService.getPrimeNgLanguageData(true),
    });

    this.toastMessageService
      .getToastMessageObs()
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: (message) => {
          if (message) {
            this.messageService.add(message);
          }
        },
      });

    // Subscribe to account switch events (403 "user not registered" scenarios)
    this.accountSwitchService.onAccountSwitchRequired$
      .pipe(takeUntil(this.destroying$))
      .subscribe((event: AccountSwitchEvent) => {
        this.showAccountSwitchDialog(event);
      });

    // Keep UI state consistent with route changes (avoid hard reloads)
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        const path = window.location.pathname;
        if (path === '/tenant-selection') {
          this.accountSwitchService.setTenantSelectionMode(true);
          this.isTenantSwitched = false;
          this.isInitializing = false;
          this.roleContextService.clearActiveRoleForCurrentTenant();
        }
      });

    // IMPORTANT: Handle redirect response first. Without this, inProgress$ may never return to None
    // and we can get stuck in an interaction_in_progress loop after login redirects.
    this.msalService
      .handleRedirectObservable()
      .pipe(take(1), timeout(this.AUTH_TIMEOUT_MS), takeUntil(this.destroying$))
      .subscribe({
        next: (result) => {
          if (result?.account) {
            this.msalService.instance.setActiveAccount(result.account);
            persistStoredAccountSelection(result.account);
          }
          this.beginAuthInitializationWatch();
        },
        error: (error) => {
          console.error('MSAL redirect handling failed before initialization:', error);
          this.beginAuthInitializationWatch();
        },
      });
  }

  private beginAuthInitializationWatch(): void {
    // Wait for MSAL to complete any ongoing interactions before starting auth flow
    // Race between MSAL ready and timeout to prevent infinite loading on corrupted cache
    const msalReady$ = this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
    );

    const timeout$ = timer(this.AUTH_TIMEOUT_MS);

    race(msalReady$, timeout$)
      .pipe(takeUntil(this.destroying$), take(1))
      .subscribe((result) => {
        if (typeof result === 'number') {
          // Timeout reached - MSAL is probably in a corrupted state
          console.warn('MSAL initialization timeout reached - clearing auth state and forcing login');
          this.handleMsalTimeout();
        } else if (!this.authFlowInProgress) {
          this.authFlowInProgress = true;
          this.initializeAuthFlow();
        }
      });
  }

  /**
   * Show the account switch dialog when a 403 error indicates the user needs to switch accounts
   */
  private showAccountSwitchDialog(event: AccountSwitchEvent): void {
    // Prevent multiple dialogs
    if (this.accountSwitchDialogRef) {
      return;
    }

    this.accountSwitchDialogRef = this.dialogService.open(AccountSwitchDialogComponent, {
      header: this.dataService.getTexts().AccountSwitchDialogHeaderRequired,
      width: '450px',
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: false,
      data: {
        event: event,
      },
    });

    this.accountSwitchDialogRef.onClose.pipe(takeUntil(this.destroying$)).subscribe((result: any) => {
      this.accountSwitchDialogRef = null;

      if (!result) {
        // If user closed without action, show a toast reminder
        this.toastMessageService.showWarningMessage(
          this.dataService.getTexts().AccountSwitchToastRecommendedTitle,
          this.dataService.getTexts().AccountSwitchToastRecommendedMessage,
        );
        return;
      }

      if (result.action === 'select_tenant') {
        clearStoredAccountSelection(localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID));
        localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
        this.roleContextService.clearActiveRoleForCurrentTenant();
        this.isTenantSwitched = false;
        this.isInitializing = true;
        // Enable tenant selection mode to prevent 403 dialog loops
        this.accountSwitchService.setTenantSelectionMode(true);
        this.manageTenantRedirect();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private async initializeAuthFlow(): Promise<void> {
    // Reset session expired flag before each auth attempt
    this.sessionExpired = false;
    const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    this.isOnboarding = sessionStorage.getItem(SESSION_STORAGE_KEYS.ONBOARDING_ACTIVE) !== null;

    if (this.checkIfInstanceAdmin()) {
      this.isInitializing = false;
      return;
    }

    if (this.checkIfConsent()) {
      this.isInitializing = false;
      return;
    }

    await this.checkExistingAuthentication(storedTenantId);
  }

  private isLandingLikeRoute(): boolean {
    const path = window.location.pathname;
    return path === '/' || path === '/landing' || path === '/tenant-selection';
  }

  private getActiveRoles(): string[] {
    const accounts = this.msalService.instance.getAllAccounts();
    const account = this.msalService.instance.getActiveAccount() ?? accounts[0];
    const roles = (account?.idTokenClaims?.roles as string[] | undefined) ?? [];
    return roles.map((r) => r.toLowerCase()).filter((r) => r.trim().length > 0);
  }

  private async waitForRolesProvisioning(maxWaitMs = 4000): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      const roles = this.getActiveRoles();
      if (roles.length > 0) {
        return;
      }

      try {
        // Force-refresh token once; if roles were just assigned, a fresh token may contain them.
        await firstValueFrom(this.authorizationService.acquireToken([SCOPES.USER_IMPERSONATION], undefined, true));
      } catch {
        // ignore - we'll fall back to tenant selection
      }

      // small delay before next check
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }

  private async navigateToDefaultAreaIfNeeded(): Promise<void> {
    if (!this.isLandingLikeRoute()) {
      return;
    }

    if (this.isOnboarding || this.isConsent || this.isInstanceAdmin) {
      return;
    }

    await this.waitForRolesProvisioning();
    const roles = this.getActiveRoles();

    // No roles means: either user is not registered in DB for this tenant OR role propagation is delayed.
    // We fail safe: go back to tenant selection (no redirect loops).
    if (!roles || roles.length === 0) {
      localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
      this.accountSwitchService.setTenantSelectionMode(true);
      this.isTenantSwitched = false;
      this.roleContextService.clearActiveRoleForCurrentTenant();
      await this.router.navigateByUrl('/tenant-selection', { replaceUrl: true });
      return;
    }

    this.roleContextService.refreshActiveRoleFromStorage();
    const availableRoles = this.roleContextService.getAvailableRoles();
    const preferredRole = this.roleContextService.chooseDefaultRole(availableRoles);

    if (!preferredRole) {
      localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
      this.accountSwitchService.setTenantSelectionMode(true);
      this.isTenantSwitched = false;
      await this.router.navigateByUrl('/tenant-selection', { replaceUrl: true });
      return;
    }

    this.roleContextService.setActiveRole(preferredRole);

    const routeByRole: Record<PortalRole, string> = {
      instanceadmin: '/in',
      admin: '/admin',
      user: '/user',
    };

    await this.router.navigateByUrl(routeByRole[preferredRole], { replaceUrl: true });
  }

  private async checkExistingAuthentication(storedTenantId: string | null): Promise<void> {
    try {
      await this.msalService.instance.initialize();

      const accounts = this.msalService.instance.getAllAccounts();

      // Validate accounts have required claims (detect corrupted cache)
      const validAccounts = accounts.filter((acc) => acc.idTokenClaims && acc.tenantId && acc.homeAccountId);

      // Check if we have corrupted accounts that need cleanup
      if (accounts.length > 0 && validAccounts.length === 0) {
        console.warn('Found corrupted MSAL accounts without valid claims, clearing cache');
        this.clearAllAuthState();
        this.markSessionExpired();
        this.isInitializing = false;
        this.auth.loginRedirect();
        return;
      }

      // Check for expired tokens
      if (validAccounts.length > 0) {
        const hasNonExpiredAccount = validAccounts.some((acc) => {
          const exp = acc.idTokenClaims?.['exp'] as number | undefined;
          if (!exp) return true; // No exp claim = assume valid
          const nowInSeconds = Math.floor(Date.now() / 1000);
          return exp > nowInSeconds + CLOCK_SKEW_BUFFER_SECONDS;
        });

        if (!hasNonExpiredAccount) {
          this.clearAllAuthState();
          this.markSessionExpired();
          this.isInitializing = false;
          this.auth.loginRedirect();
          return;
        }
      }

      // No accounts at all - need fresh login
      if (validAccounts.length === 0) {
        // Set isInitializing to false before redirect to prevent page freeze
        // If redirect fails, user will see the page instead of being stuck on loading screen
        this.markSessionExpired();
        this.isInitializing = false;
        try {
          this.auth.loginRedirect();
        } catch (error) {
          console.error('Error during login redirect:', error);
          // If redirect fails, show error and allow user to retry
          this.toastMessageService.showErrorMessage(
            this.dataService.getTexts().TenantAuthenticationFailedTitle || 'Authentication Error',
            'Login redirect failed. Please try refreshing the page.',
          );
        }
        return;
      }

      // If no tenant is stored, user must select a tenant first
      // Do NOT auto-select - show tenant selection UI
      if (!storedTenantId) {
        // Set up active account with first available for tenant discovery API calls
        const defaultAccount = this.msalService.instance.getActiveAccount() ?? accounts[0];
        this.msalService.instance.setActiveAccount(defaultAccount);

        this.accountSwitchService.setTenantSelectionMode(true);
        await this.manageTenantRedirect();
        return;
      }

      // We have a stored tenant - try to use it
      let targetAccount = resolveAccountForTenant(
        accounts,
        this.msalService.instance.getActiveAccount(),
        storedTenantId,
      );

      if (!targetAccount) {
        if (readStoredAccountHomeId(storedTenantId)) {
          // A saved person has disappeared from the cache. Ask for an explicit
          // sign-in rather than rediscovering and auto-selecting another person.
          const switchResult = await this.auth.switchToAnotherTenant(storedTenantId);
          if (switchResult !== 'redirect') this.getTokenAfterLogin(storedTenantId);
          return;
        }
        // Stored tenant doesn't match any account - clear and show tenant selection
        clearStoredAccountSelection(storedTenantId);
        localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
        const defaultAccount = this.msalService.instance.getActiveAccount() ?? accounts[0];
        this.msalService.instance.setActiveAccount(defaultAccount);
        persistStoredAccountSelection(defaultAccount);
        this.accountSwitchService.setTenantSelectionMode(true);
        await this.manageTenantRedirect();
        return;
      }

      this.msalService.instance.setActiveAccount(targetAccount);
      persistStoredAccountSelection(targetAccount);
      setMsalTenantId(storedTenantId);

      const hasValidToken = await this.authorizationService.hasValidToken();

      if (hasValidToken) {
        // User is authenticated with the correct tenant
        this.selectedTenantId = storedTenantId;
        this.isTenantSwitched = true;
        this.isInitializing = false;
        await this.navigateToDefaultAreaIfNeeded();
        return;
      }

      // Token validation failed - try to acquire token
      try {
        await firstValueFrom(
          this.authorizationService.acquireToken(
            [SCOPES.USER_IMPERSONATION],
            `https://login.microsoftonline.com/${storedTenantId}/`,
          ),
        );

        // Success - update account reference
        const updatedAccount = resolveAccountForTenant(
          this.msalService.instance.getAllAccounts(),
          this.msalService.instance.getActiveAccount(),
          storedTenantId,
        );
        if (updatedAccount) {
          this.msalService.instance.setActiveAccount(updatedAccount);
        }

        this.selectedTenantId = storedTenantId;
        this.isTenantSwitched = true;
        this.isInitializing = false;
        await this.navigateToDefaultAreaIfNeeded();
        return;
      } catch (tokenError) {
        console.warn('Token acquisition for stored tenant failed:', tokenError);
        this.toastMessageService.showErrorMessage(
          this.dataService.getTexts().TenantAuthenticationFailedTitle,
          this.dataService.getTexts().TenantAuthenticationFailedMessage,
        );
        // Clear invalid tenant and show tenant selection
        clearStoredAccountSelection(storedTenantId);
        localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
        this.accountSwitchService.setTenantSelectionMode(true);
        await this.manageTenantRedirect();
      }
    } catch (error) {
      console.error('Error during authentication check:', error);
      // On critical error, reset state and prompt for fresh login
      this.handleAuthError();
    }
  }

  private handleAuthError(): void {
    // Clear potentially corrupted state
    this.authorizationService.clearAuthState();
    clearStoredAccountSelection(localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID));
    localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
    this.roleContextService.clearActiveRoleForCurrentTenant();
    this.markSessionExpired();
    this.isInitializing = false;
    // Redirect to login
    try {
      this.auth.loginRedirect();
    } catch (error) {
      console.error('Error during login redirect from handleAuthError:', error);
      this.toastMessageService.showErrorMessage(
        this.dataService.getTexts().TenantAuthenticationFailedTitle || 'Authentication Error',
        'Login redirect failed. Please try refreshing the page.',
      );
    }
  }

  /**
   * Handle MSAL initialization timeout - cache is likely corrupted
   */
  private handleMsalTimeout(): void {
    // If we still have an auth response hash, try one recovery pass first.
    // This specifically fixes "stuck on loading + interaction_in_progress" after redirect callback.
    if (this.hasPendingMsalHashResponse() && !this.redirectRecoveryAttempted) {
      this.redirectRecoveryAttempted = true;
      console.warn('MSAL timeout with auth hash present - attempting redirect response recovery');
      this.recoverFromPendingRedirectHash();
      return;
    }

    this.forceFreshLoginAfterTimeout();
  }

  private hasPendingMsalHashResponse(): boolean {
    const hash = window.location.hash || '';
    return hash.includes('code=') || hash.includes('error=') || hash.includes('id_token=') || hash.includes('state=');
  }

  private recoverFromPendingRedirectHash(): void {
    this.msalService
      .handleRedirectObservable()
      .pipe(take(1), timeout(this.AUTH_TIMEOUT_MS), takeUntil(this.destroying$))
      .subscribe({
        next: async (result) => {
          if (result?.account) {
            this.msalService.instance.setActiveAccount(result.account);
          }

          this.redirectRecoveryAttempted = false;
          this.sessionExpired = false;
          this.isInitializing = true;
          this.authFlowInProgress = false;
          await this.initializeAuthFlow();
        },
        error: (error) => {
          console.error('MSAL redirect recovery failed:', error);
          this.forceFreshLoginAfterTimeout();
        },
      });
  }

  private forceFreshLoginAfterTimeout(): void {
    console.warn('Clearing potentially corrupted MSAL state due to timeout');
    this.clearAllAuthState();
    this.markSessionExpired();
    this.isInitializing = false;
    this.authFlowInProgress = true; // Prevent further auth attempts

    // Redirect to login
    try {
      this.auth.loginRedirect();
    } catch (error) {
      console.error('Error during login redirect from handleMsalTimeout:', error);
      // Prevent permanent loading screen if redirect cannot be started.
      this.sessionExpired = false;
      this.toastMessageService.showErrorMessage(
        this.dataService.getTexts().TenantAuthenticationFailedTitle || 'Authentication Error',
        'Session expired. Please refresh the page to login again.',
      );
    }
  }

  /**
   * Clear all authentication state including MSAL cache
   */
  private clearAllAuthState(): void {
    this.organizationContext.clear();
    // 1. Reset MSAL active account
    try {
      this.msalService.instance.setActiveAccount(null);
    } catch (e) {
      console.error('Error resetting MSAL account:', e);
    }

    // 2. Clear all MSAL-related localStorage entries
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('msal.') ||
          key.includes('.idtoken') ||
          key.includes('.accesstoken') ||
          key.includes('.refreshtoken') ||
          key.includes('login.windows.net'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // 3. Clear app-specific keys
    localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
    this.roleContextService.clearActiveRoleForCurrentTenant();
    this.hasAutoSelectedSingleTenant = false;
    clearAllStoredAccountSelections();

    // 4. Also use AuthorizationService's clearAuthState
    this.authorizationService.clearAuthState();

    console.info(`Cleared ${keysToRemove.length} MSAL cache entries and app auth state`);
  }

  private markSessionExpired(): void {
    this.sessionExpired = true;
    this.selectedTenantId = undefined;
    this.connectedTenants = [];
    this.hasAutoSelectedSingleTenant = false;
  }

  checkIfInstanceAdmin(): boolean {
    const path = window.location.pathname;
    if (path !== '/in' && !path.startsWith('/in/')) return false;

    const account = prepareInstanceAdminEntry(this.msalService.instance);
    if (!account) return false;

    this.selectedTenantId = account.tenantId;
    this.isInstanceAdmin = true;
    this.isTenantSwitched = true;
    return true;
  }

  checkIfConsent(): boolean {
    let currentUrl = window.location.href;
    let consentRoute = environment.frontendUrl + '/consent';
    if (currentUrl.substring(0, consentRoute.length) === consentRoute) {
      this.isConsent = true;
      return true;
    }
    return false;
  }

  async manageTenantRedirect(): Promise<void> {
    // Guard: Verify we're still initializing
    if (!this.isInitializing) {
      console.warn('manageTenantRedirect: isInitializing is false, skipping');
      return;
    }

    let onboarding = await this.checkIfOnboarding();

    if (!onboarding) {
      // Fetch tenant list with timeout to prevent infinite hanging
      const tenantDiscovery$ = this.httpClient
        .get<TenantResponse>('https://management.azure.com/tenants?api-version=2022-12-01')
        .pipe(
          retry(1), // Retry once on failure
          timeout(this.TENANT_DISCOVERY_TIMEOUT_MS),
          takeUntil(this.destroying$),
        );

      tenantDiscovery$.subscribe({
        next: (tenantResponse: TenantResponse) => {
          let azureTenants: AzureTenant[] = tenantResponse.value;

          this.httpClient
            .put<ConnectedTenants>(environment.baseUrl + 'tenants/connected', { tenants: azureTenants })
            .pipe(timeout(5000), takeUntil(this.destroying$))
            .subscribe({
              next: (connectedTenants: ConnectedTenants) => {
                this.handleConnectedTenants(connectedTenants);
              },
              error: (error) => {
                console.error('Failed to sync connected tenants:', error);
                this.isInitializing = false;
              },
            });
        },
        error: (error) => {
          console.error('Tenant discovery failed:', error);
          // Don't block - proceed without tenant sync
          this.isInitializing = false;
        },
      });
    } else {
      this.isInitializing = false;
    }
  }

  async checkIfOnboarding(): Promise<boolean> {
    let currentUrl = window.location.href;
    if (currentUrl.includes('onboarding')) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.ONBOARDING_ACTIVE, 'true');
      this.isOnboarding = true;
      if (localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID) != null) {
        let tenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID)!;
        // The shared switch lifecycle invalidates requests before changing storage.
        await this.switchTenant(tenantId);
      } else {
        this.isTenantSwitched = true;
      }
      return true;
    }
    return false;
  }

  async switchTenant(tenant: string): Promise<'silent' | 'redirect'> {
    const result = await this.auth.switchToAnotherTenant(tenant);
    this.isTenantSwitched = result === 'silent';
    return result;
  }

  async handleConnectedTenants(connectedTenants: ConnectedTenants): Promise<void> {
    this.connectedTenants = connectedTenants.tenants;

    // Reset the one-shot guard whenever tenant cardinality changes away from single-tenant.
    // Intentional: if tenant access later becomes exactly one again (e.g., account switch or changed permissions),
    // we allow one fresh auto-select attempt for that new state.
    if (this.connectedTenants.length !== 1) {
      this.hasAutoSelectedSingleTenant = false;
    }

    if (this.connectedTenants.length === 0) {
      this.isNoTenantConnected = true;
      this.isTenantSwitched = false;
      const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
      if (storedTenantId) {
        clearStoredAccountSelection(storedTenantId);
      }
      localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
      this.selectedTenantId = undefined;
      this.roleContextService.clearActiveRoleForCurrentTenant();
      this.accountSwitchService.setTenantSelectionMode(true);
      this.isInitializing = false;
      return;
    } else if (this.connectedTenants.length === 1) {
      const onlyTenantId = this.connectedTenants[0].tenantId;

      // If no tenant is stored, we do one auto-select attempt to avoid flashing tenant selection UI.
      // Then we fall back to tenant selection mode to avoid redirect loops when auth/roles are missing.
      const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
      if (!storedTenantId) {
        if (!this.hasAutoSelectedSingleTenant) {
          this.hasAutoSelectedSingleTenant = true;
          this.accountSwitchService.setTenantSelectionMode(false);
          await this.selectTenant(onlyTenantId);
          return;
        }

        this.accountSwitchService.setTenantSelectionMode(true);
        this.isInitializing = false;
        return;
      }

      // Stored tenant exists: validate and proceed.
      if (storedTenantId !== onlyTenantId) {
        console.warn('handleConnectedTenants: Stored tenant does not match only connected tenant, clearing');
        localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
        this.accountSwitchService.setTenantSelectionMode(true);
        this.isInitializing = false;
        return;
      }

      this.dataService.setCurrentTenantName(this.connectedTenants[0].displayName);
      const accounts = this.msalService.instance.getAllAccounts();
      const activeAccount = this.msalService.instance.getActiveAccount() ?? accounts[0];

      if (!activeAccount) {
        this.isInitializing = false;
        this.auth.loginRedirect();
        return;
      }

      if (activeAccount.tenantId?.includes(onlyTenantId)) {
        persistStoredAccountSelection(activeAccount);
        setMsalTenantId(onlyTenantId);
        this.getTokenAfterLogin(onlyTenantId);
      } else {
        const switchResult = await this.auth.switchToAnotherTenant(onlyTenantId);
        if (switchResult === 'redirect') {
          return;
        }
        this.getTokenAfterLogin(onlyTenantId);
      }
    } else {
      // Multiple tenants - check if user has a stored preference
      let tenantIdInStorage = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
      if (!tenantIdInStorage) {
        // No stored tenant - show tenant selection UI
        this.accountSwitchService.setTenantSelectionMode(true);
        this.isInitializing = false;
        // isTenantSwitched stays false -> tenant selection UI will be shown
      } else {
        // User has a stored tenant - validate it exists in connected tenants
        const selectedTenant = this.connectedTenants.find((t) => t.tenantId === tenantIdInStorage);
        if (selectedTenant) {
          // Stored tenant is valid - use it
          setMsalTenantId(tenantIdInStorage);
          this.dataService.setCurrentTenantName(selectedTenant.displayName);
          this.getTokenAfterLogin(tenantIdInStorage);
        } else {
          // Stored tenant ID is invalid (no longer in connected tenants) - clear it and show tenant selection UI
          console.warn(
            'handleConnectedTenants: Stored tenant ID not found in connected tenants, clearing and showing selection UI',
          );
          localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
          this.accountSwitchService.setTenantSelectionMode(true);
          this.isInitializing = false;
          // isTenantSwitched stays false -> tenant selection UI will be shown
        }
      }
    }
  }

  // Get token without user interaction
  getTokenAfterLogin(tenantId: string): void {
    // Check if we already have a valid token for this tenant
    const accounts = this.msalService.instance.getAllAccounts();
    const accountForTenant = resolveAccountForTenant(accounts, this.msalService.instance.getActiveAccount(), tenantId);

    if (accountForTenant) {
      this.msalService.instance.setActiveAccount(accountForTenant);
      persistStoredAccountSelection(accountForTenant);
    }

    // Try silent token acquisition with timeout
    this.authorizationService
      .acquireToken([SCOPES.USER_IMPERSONATION], `https://login.microsoftonline.com/${tenantId}/`)
      .pipe(timeout(10000), takeUntil(this.destroying$))
      .subscribe({
        next: async () => {
          const newAccount = resolveAccountForTenant(
            this.msalService.instance.getAllAccounts(),
            this.msalService.instance.getActiveAccount(),
            tenantId,
          );

          if (newAccount) {
            this.msalService.instance.setActiveAccount(newAccount);
            persistStoredAccountSelection(newAccount);
          }
          this.isTenantSwitched = true;
          this.isInitializing = false;
          await this.navigateToDefaultAreaIfNeeded();
        },
        error: (error) => {
          console.error('Silent token acquisition failed:', error);
          // Token acquisition failed - clear corrupted state and redirect to fresh login
          // This prevents freeze loops caused by corrupted auth state
          this.clearAllAuthState();
          this.markSessionExpired();
          this.isInitializing = false;
          try {
            this.auth.loginRedirect();
          } catch (e) {
            console.error('Login redirect failed:', e);
            // Fallback: show error message instead of freezing
            this.toastMessageService.showErrorMessage(
              this.dataService.getTexts().TenantAuthenticationFailedTitle,
              'Session reset. Please refresh the page to login again.',
            );
          }
        },
      });
  }

  logout(): void {
    this.organizationContext.clear();
    clearStoredAccountSelection(localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID));
    localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
    this.roleContextService.clearActiveRoleForCurrentTenant();
    localStorage.removeItem('currentUser');
    this.msalService.logout();
  }

  // Method to handle tenant selection
  async selectTenant(id: string) {
    if (id != undefined) {
      // Disable tenant selection mode when user selects a tenant
      this.accountSwitchService.setTenantSelectionMode(false);
      try {
        const switchResult = await this.auth.switchToAnotherTenant(id);
        if (switchResult === 'redirect') return;
        this.dataService.setCurrentTenantName(this.connectedTenants.find((t) => t.tenantId == id).displayName);
        this.getTokenAfterLogin(id);
      } catch {
        // The auth service owns recovery. Do not overwrite a newer tab/logout selection.
        this.toastMessageService.showErrorMessage(
          this.dataService.getTexts().TenantAuthenticationFailedTitle,
          this.dataService.getTexts().TenantAuthenticationFailedMessage,
        );
      }
    }
  }
}
